import { NavLink } from "react-router-dom";
import {
  Activity,
  AlertTriangle,
  BarChart3,
  Compass,
  DollarSign,
  Home as HomeIcon,
  Inbox,
  LogOut,
  Mail,
  MessagesSquare,
  Settings2,
  ShieldCheck,
  Smartphone,
  Users,
  UserCheck,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useAuth } from "@/lib/AuthContext";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { useRealtime } from "@/lib/realtime";
import { Avatar, AvatarFallback, AvatarImage } from "./ui/avatar";

interface NavItem {
  to: string;
  label: string;
  icon: typeof AlertTriangle;
  end?: boolean;
}

const items: NavItem[] = [
  { to: "/", label: "Home", icon: HomeIcon, end: true },
  { to: "/triage", label: "Triage", icon: AlertTriangle },
  { to: "/drafts", label: "Drafts", icon: Mail },
  { to: "/activity", label: "Activity", icon: Activity },
  { to: "/creators", label: "Creators", icon: Users },
  { to: "/ultradash", label: "Ultradash", icon: MessagesSquare },
  { to: "/ultradash/queue", label: "UD Queue", icon: ShieldCheck },
  { to: "/recruiters", label: "Recruiters", icon: UserCheck },
  { to: "/fleet", label: "Fleet", icon: Smartphone },
  { to: "/recruiting", label: "Recruiting", icon: Compass },
  { to: "/costs", label: "Costs", icon: DollarSign },
  { to: "/system", label: "System", icon: Settings2 },
];

export function LeftNav() {
  const { user, signOut } = useAuth();

  // Triage badge: number of pending drafts (most actionable). Polled cheaply
  // and bumped on realtime drafts_pending events.
  const triageQ = useQuery({
    queryKey: ["triage", "count"],
    queryFn: () => api.triage().then((items) => items.length),
    refetchInterval: 30_000,
  });
  const draftsQ = useQuery({
    queryKey: ["drafts", "count"],
    queryFn: () => api.drafts().then((items) => items.length),
    refetchInterval: 30_000,
  });
  const udQueueQ = useQuery({
    queryKey: ["ultradash", "drafts", "count"],
    queryFn: () =>
      api.ultradashDrafts({ status: "pending_review", limit: 500 })
        .then((d) => d.items.length),
    refetchInterval: 20_000,
  });
  useRealtime("drafts_pending", () => {
    triageQ.refetch();
    draftsQ.refetch();
  });
  // Polling at 20s is enough for the UD Queue badge; the realtime event is a
  // custom event (not a table), so we don't hook useRealtime here.

  return (
    <nav className="flex h-full flex-col">
      <div className="flex items-center gap-2 px-4 pb-2 pt-4">
        <div className="grid h-7 w-7 place-items-center rounded-md bg-brand text-brand-foreground shadow-sm">
          <Inbox className="h-4 w-4" />
        </div>
        <div className="flex-1 leading-tight">
          <div className="text-sm font-semibold">Kitt</div>
          <div className="text-2xs text-muted-foreground">admin</div>
        </div>
      </div>

      <div className="mt-2 flex-1 space-y-0.5 px-2">
        {items.map(({ to, label, icon: Icon, end }) => (
          <NavLink
            key={to}
            to={to}
            end={end}
            className={({ isActive }) =>
              cn(
                "group flex items-center gap-2.5 rounded-md px-2.5 py-1.5 text-sm",
                "transition-colors hover:bg-accent/60 hover:text-accent-foreground",
                isActive
                  ? "bg-accent text-accent-foreground shadow-sm"
                  : "text-muted-foreground",
              )
            }
          >
            <Icon className="h-3.5 w-3.5 opacity-90" />
            <span className="flex-1">{label}</span>
            {label === "Triage" && triageQ.data ? (
              <span className="rounded-full bg-brand/15 px-1.5 text-2xs font-semibold text-brand ring-1 ring-inset ring-brand/30">
                {triageQ.data}
              </span>
            ) : null}
            {label === "Drafts" && draftsQ.data ? (
              <span className="rounded-full bg-amber-500/20 px-1.5 text-2xs font-semibold text-amber-700 ring-1 ring-inset ring-amber-500/40">
                {draftsQ.data}
              </span>
            ) : null}
            {label === "UD Queue" && udQueueQ.data ? (
              <span className="rounded-full bg-purple-500/20 px-1.5 text-2xs font-semibold text-purple-700 ring-1 ring-inset ring-purple-500/40">
                {udQueueQ.data}
              </span>
            ) : null}
          </NavLink>
        ))}
      </div>

      <div className="border-t border-border p-2">
        <div className="flex items-center gap-2 rounded-md px-2 py-1.5">
          <Avatar className="h-7 w-7">
            <AvatarImage src={user?.avatar_url ?? undefined} alt="" />
            <AvatarFallback>
              {(user?.email ?? "??").slice(0, 2).toUpperCase()}
            </AvatarFallback>
          </Avatar>
          <div className="min-w-0 flex-1 text-xs leading-tight">
            <div className="truncate font-medium">{user?.display_name ?? user?.email}</div>
            <div className="truncate text-2xs text-muted-foreground">{user?.role}</div>
          </div>
          <button
            type="button"
            onClick={signOut}
            title="Sign out"
            className="rounded p-1 text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
          >
            <LogOut className="h-3.5 w-3.5" />
          </button>
        </div>
        <div className="px-2 pb-1 pt-2 text-2xs text-muted-foreground/60">
          <BarChart3 className="mr-1 inline h-3 w-3" /> v1
        </div>
      </div>
    </nav>
  );
}
